import Config from 'common-src/config';
import { apiRequest, getRestErrorMessage } from 'common-src/features/rest';
import { HttpMethods } from 'common-src/features/rest/constants';

import { TASK_QUEUE_PAGE_SIZE, TASK_QUEUE_SORT_MODE } from '../constants';
import { normalizeEscalationPayload } from '../helpers';

const DEFAULT_CARE_ORCHESTRATION_TRIGGER_PAYLOAD = {
  type_id: 11,
  reason_id: 12,
  subject: 'Care Coordination Task',
  status: 1,
  priority: 'high',
  team: 'ops',
};

const extractResponseBody = async (response) => {
  const body = await response.json();

  if (!response.ok) {
    throw new Error(getRestErrorMessage(body) || `Request failed with status ${response.status}`);
  }

  return body;
};

const assertOk = async (response) => {
  if (response.ok) return;

  const errorBody = await response.json().catch(() => null);
  throw new Error(
    getRestErrorMessage(errorBody) || `Request failed with status ${response.status}`,
  );
};

export const fetchTaskQueueData = async ({
  pageSize = TASK_QUEUE_PAGE_SIZE,
  offset = 0,
  sortMode = TASK_QUEUE_SORT_MODE.PatientPriority,
  team = 'all',
  ownerScope = 'all',
  statusScope = 'open',
  mrn = null,
  organizationScope = null,
  departmentAthenaIds = null,
  evidenceType = null,
} = {}) => {
  const taskQueueRes = await apiRequest({
    endpoint: '../v2/dashboard/taskQueue',
    queryParams: {
      limit: pageSize,
      offset,
      sort_mode: sortMode,
      team,
      owner_scope: ownerScope,
      status_scope: statusScope,
      ...(mrn ? { mrn } : {}),
      ...(organizationScope ? { organization_scope: organizationScope } : {}),
      ...(departmentAthenaIds?.length ? { department_athena_ids: departmentAthenaIds } : {}),
      ...(evidenceType ? { evidence_type: evidenceType } : {}),
    },
  });
  const taskQueueBody = await extractResponseBody(taskQueueRes);
  const taskQueueRows = taskQueueBody?.data || [];
  const responseMeta = taskQueueBody?.meta || null;
  const hasReachedEndOfFilteredResults = taskQueueRows.length < pageSize;
  const normalizedTaskQueueMeta =
    responseMeta && hasReachedEndOfFilteredResults
      ? {
          ...responseMeta,
          total: Math.min(
            responseMeta?.total ?? offset + taskQueueRows.length,
            offset + taskQueueRows.length,
          ),
        }
      : responseMeta;

  return {
    taskQueueRows,
    taskQueueMeta: normalizedTaskQueueMeta,
  };
};

export const fetchTaskQueueMetaData = async ({
  pageSize = TASK_QUEUE_PAGE_SIZE,
  offset = 0,
  team = 'all',
  ownerScope = 'all',
  statusScope = 'open',
  mrn = null,
  organizationScope = null,
  departmentAthenaIds = null,
} = {}) => {
  const response = await apiRequest({
    endpoint: '../v2/dashboard/taskQueue/meta',
    queryParams: {
      limit: pageSize,
      offset,
      team,
      owner_scope: ownerScope,
      status_scope: statusScope,
      ...(mrn ? { mrn } : {}),
      ...(organizationScope ? { organization_scope: organizationScope } : {}),
      ...(departmentAthenaIds?.length ? { department_athena_ids: departmentAthenaIds } : {}),
    },
  });

  return extractResponseBody(response);
};

export const fetchTaskSearchPatients = async ({
  query,
  team = 'all',
  ownerScope = 'all',
  statusScope = 'open',
  pageSize = 20,
  offset = 0,
} = {}) => {
  const response = await apiRequest({
    endpoint: '../v2/dashboard/task-search-patients',
    queryParams: {
      q: query,
      team,
      owner_scope: ownerScope,
      status_scope: statusScope,
      limit: pageSize,
      offset,
    },
  });
  const body = await extractResponseBody(response);

  return {
    rows: body?.data || [],
    meta: body?.meta || null,
  };
};

export const fetchTaskSubtasks = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/subtasks`,
  });
  const body = await extractResponseBody(response);

  return body?.data || [];
};

export const fetchTaskSubtasksBulk = async (parentTaskIds) => {
  const response = await apiRequest({
    endpoint: '../v2/dashboard/taskQueue/subtasks',
    method: HttpMethods.Post,
    body: { parentTaskIds },
  });
  const body = await extractResponseBody(response);

  return body?.data || {};
};

export const fetchDashboardSupplementalData = async ({
  organizationScope = null,
  readinessDateFrom = null,
  readinessDateTo = null,
} = {}) => {
  const readinessAppointmentsRes = await apiRequest({
    endpoint: '../v2/dashboard/readinessAppointments',
    queryParams: {
      ...(organizationScope ? { organization_scope: organizationScope } : {}),
      ...(readinessDateFrom ? { date_from: readinessDateFrom } : {}),
      ...(readinessDateTo ? { date_to: readinessDateTo } : {}),
    },
  });

  const readinessAppointmentsBody = await extractResponseBody(readinessAppointmentsRes);
  const appointmentsBody = readinessAppointmentsBody;

  return { appointmentsBody, readinessAppointmentsBody };
};

export const fetchDashboardStatsData = async () => {
  const response = await apiRequest({ endpoint: '../v2/dashboard/stats' });

  return extractResponseBody(response);
};

export const fetchReadinessAppointmentsData = async ({
  organizationScope = null,
  workflowScope = null,
  readinessDateFrom = null,
  readinessDateTo = null,
} = {}) => {
  const response = await apiRequest({
    endpoint: '../v2/dashboard/readinessAppointments',
    queryParams: {
      ...(organizationScope ? { organization_scope: organizationScope } : {}),
      ...(workflowScope ? { workflow_scope: workflowScope } : {}),
      ...(readinessDateFrom ? { date_from: readinessDateFrom } : {}),
      ...(readinessDateTo ? { date_to: readinessDateTo } : {}),
    },
  });

  return extractResponseBody(response);
};

export const fetchTaskQueueChanges = async (
  since,
  {
    team = 'all',
    ownerScope = 'all',
    statusScope = 'open',
    mrn = null,
    organizationScope = null,
    departmentAthenaIds = null,
  } = {},
) => {
  const response = await apiRequest({
    endpoint: '../v2/dashboard/taskQueue/changes',
    queryParams: {
      since,
      team,
      owner_scope: ownerScope,
      status_scope: statusScope,
      ...(mrn ? { mrn } : {}),
      ...(organizationScope ? { organization_scope: organizationScope } : {}),
      ...(departmentAthenaIds?.length ? { department_athena_ids: departmentAthenaIds } : {}),
    },
  });

  return extractResponseBody(response);
};

export const fetchTaskQueueItemById = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}`,
  });

  return extractResponseBody(response);
};

export const fetchPatientsVisitHistory = async (patientIds) => {
  const response = await apiRequest({
    endpoint: '../v2/dashboard/visitHistory',
    method: HttpMethods.Post,
    body: { patientIds },
  });

  return extractResponseBody(response);
};

export const assignTaskToMe = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/assignMe`,
    method: HttpMethods.Post,
  });
  return extractResponseBody(response);
};

export const followTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task/${taskId}/follow`,
    method: HttpMethods.Post,
  });
  await assertOk(response);
};

export const unfollowTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task/${taskId}/unfollow`,
    method: HttpMethods.Post,
  });
  await assertOk(response);
};

export const blockTask = async (taskId, blockedReason) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/block`,
    method: HttpMethods.Post,
    body: { blockedReason },
  });
  return extractResponseBody(response);
};

export const fetchHuddleNotesBulk = async ({
  dateFrom = null,
  dateTo = null,
  organizationScope = null,
} = {}) => {
  const response = await apiRequest({
    endpoint: '../v2/dashboard/huddleNotes',
    method: HttpMethods.Get,
    queryParams: {
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
      ...(organizationScope ? { organization_scope: organizationScope } : {}),
    },
  });
  return extractResponseBody(response);
};

export const postHuddleNote = async (appointmentId, text) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/appointments/${appointmentId}/huddleNotes`,
    method: HttpMethods.Post,
    body: { text },
  });
  return extractResponseBody(response);
};

export const markTaskComplete = async (taskId, completionPayload = null) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/markComplete`,
    method: HttpMethods.Post,
    ...(completionPayload ? { body: completionPayload } : {}),
  });
  return extractResponseBody(response);
};

export const fetchLeadTaskQueue = async ({
  status = 'open',
  owner = 'all',
  search = '',
  leadResourceId = null,
  taskId = null,
  limit = null,
  offset = null,
} = {}) => {
  const response = await apiRequest({
    endpoint: '../v2/dashboard/leadTaskQueue',
    queryParams: {
      status,
      owner,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(leadResourceId ? { leadResourceId } : {}),
      ...(taskId ? { taskId } : {}),
      ...(limit != null ? { limit } : {}),
      ...(offset != null ? { offset } : {}),
    },
  });
  return extractResponseBody(response);
};

export const assignLeadTaskToMe = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/leadTaskQueue/${taskId}/assignMe`,
    method: HttpMethods.Post,
    body: {},
  });
  return extractResponseBody(response);
};

export const createPatientFromLead = async (leadResourceId) => {
  const response = await apiRequest({
    endpoint: `../v1/leadResources/${leadResourceId}/createPatient`,
    method: HttpMethods.Post,
    body: {},
  });
  return extractResponseBody(response);
};

export const sendLeadConsentForm = async (leadResourceId, { leadTaskId = null } = {}) => {
  const response = await apiRequest({
    endpoint: `../v1/leadResources/${leadResourceId}/sendConsent`,
    method: HttpMethods.Post,
    body: { informedConsent: false, ...(leadTaskId ? { leadTaskId } : {}) },
  });
  return extractResponseBody(response);
};

export const markLeadTaskComplete = async (taskId, completionPayload = null) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/leadTaskQueue/${taskId}/markComplete`,
    method: HttpMethods.Post,
    ...(completionPayload ? { body: completionPayload } : {}),
  });
  return extractResponseBody(response);
};

export const cancelTask = async (taskId, reason, cancelPayload = null) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/cancel`,
    method: HttpMethods.Post,
    body: {
      reason,
      ...(cancelPayload || {}),
    },
  });
  return extractResponseBody(response);
};

export const deferTask = async (taskId, heldUntil, reason = null, note = null) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/defer`,
    method: HttpMethods.Post,
    body: { heldUntil, ...(reason ? { reason } : {}), ...(note ? { note } : {}) },
  });
  return extractResponseBody(response);
};

export const dismissVisitTask = async (taskId, reason = null, note = null) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/dismissVisit`,
    method: HttpMethods.Post,
    body: { ...(reason ? { reason } : {}), ...(note ? { note } : {}) },
  });
  return extractResponseBody(response);
};

export const reopenTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/reopen`,
    method: HttpMethods.Post,
  });
  return extractResponseBody(response);
};

export const unblockTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/unblock`,
    method: HttpMethods.Post,
  });
  return extractResponseBody(response);
};

export const unassignTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/unassign`,
    method: HttpMethods.Post,
  });
  return extractResponseBody(response);
};

export const unassignLeadTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/leadTaskQueue/${taskId}/unassign`,
    method: HttpMethods.Post,
  });
  return extractResponseBody(response);
};

export const blockLeadTask = async (taskId, blockedReason) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/leadTaskQueue/${taskId}/block`,
    method: HttpMethods.Post,
    body: { blockedReason },
  });
  return extractResponseBody(response);
};

export const unblockLeadTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/leadTaskQueue/${taskId}/unblock`,
    method: HttpMethods.Post,
  });
  return extractResponseBody(response);
};

export const escalateLeadTask = async (taskId, escalationPayload = {}) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/leadTaskQueue/${taskId}/escalate`,
    method: HttpMethods.Post,
    body: normalizeEscalationPayload(escalationPayload),
  });
  return extractResponseBody(response);
};

export const deescalateLeadTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/leadTaskQueue/${taskId}/deescalate`,
    method: HttpMethods.Post,
  });
  return extractResponseBody(response);
};

export const reassignLeadTask = async (taskId, assigneeId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/leadTaskQueue/${taskId}/assign`,
    method: HttpMethods.Post,
    body: { assigneeId },
  });
  return extractResponseBody(response);
};

export const reassignTask = async (taskId, assigneeId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/assign`,
    method: HttpMethods.Post,
    body: { assigneeId },
  });
  return extractResponseBody(response);
};

export const bulkReassignTasks = async (assignments) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/bulkAssign`,
    method: HttpMethods.Post,
    body: { assignments },
  });
  return extractResponseBody(response);
};

export const escalateTask = async (taskId, escalationPayload = {}) => {
  const body = normalizeEscalationPayload(escalationPayload);

  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/escalate`,
    method: HttpMethods.Post,
    body,
  });
  return extractResponseBody(response);
};

export const deescalateTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/taskQueue/${taskId}/deescalate`,
    method: HttpMethods.Post,
  });
  return extractResponseBody(response);
};

export const delegateSubtasks = async (body) => {
  const response = await apiRequest({
    endpoint: '../v2/dashboard/taskQueue/delegateSubtasks',
    method: HttpMethods.Post,
    body,
  });
  return extractResponseBody(response);
};

export const triggerCareOrchestrationWorkflow = async (patientId, payload = {}) => {
  if (!patientId) {
    throw new Error('Patient ID is required');
  }

  const response = await apiRequest({
    endpoint: '../v2/dashboard/taskQueue/triggerCareOrchestrationWorkflow',
    method: HttpMethods.Post,
    body: {
      patient_id: patientId,
      ...DEFAULT_CARE_ORCHESTRATION_TRIGGER_PAYLOAD,
      ...payload,
    },
  });

  return extractResponseBody(response);
};

export const getMyTaskNotificationPreferences = async () => {
  const response = await apiRequest({
    endpoint: 'clients/me/taskNotificationPreferences',
    method: HttpMethods.Get,
  });

  return extractResponseBody(response);
};

export const patchMyTaskNotificationPreferences = async (body) => {
  const response = await apiRequest({
    endpoint: 'clients/me/taskNotificationPreferences',
    method: HttpMethods.Patch,
    body,
  });
  return extractResponseBody(response);
};

export const getMyReadinessPinnedTeam = async () => {
  const response = await apiRequest({
    endpoint: 'clients/me/readinessPinnedTeam',
    method: HttpMethods.Get,
  });

  return extractResponseBody(response);
};

export const patchMyReadinessPinnedTeam = async (pinnedTeam) => {
  const response = await apiRequest({
    endpoint: 'clients/me/readinessPinnedTeam',
    method: HttpMethods.Patch,
    body: { pinnedTeam },
  });

  return extractResponseBody(response);
};

export const getMyPinnedDepartmentScope = async () => {
  const response = await apiRequest({
    endpoint: 'clients/me/pinnedDepartmentScope',
    method: HttpMethods.Get,
  });

  return extractResponseBody(response);
};

export const patchMyPinnedDepartmentScope = async (pinnedScope) => {
  const response = await apiRequest({
    endpoint: 'clients/me/pinnedDepartmentScope',
    method: HttpMethods.Patch,
    body: { pinnedScope },
  });

  return extractResponseBody(response);
};

export const searchMyTaskNotificationSlackUsers = async (query) => {
  const response = await apiRequest({
    endpoint: 'clients/me/taskNotificationSlackUsers/search',
    method: HttpMethods.Get,
    queryParams: { query },
  });

  const body = await extractResponseBody(response);
  return body?.SlackUsers || [];
};

export const sendMyTaskNotificationTestMessage = async (body) => {
  const response = await apiRequest({
    endpoint: 'clients/me/taskNotifications/sendTestMessage',
    method: HttpMethods.Post,
    body,
  });

  return extractResponseBody(response);
};

export const getMyTaskNotifications = async () => {
  const response = await apiRequest({
    endpoint: 'clients/me/taskNotifications',
    method: HttpMethods.Get,
  });

  const body = await extractResponseBody(response);
  return body?.Notification || [];
};

export const patchMyTaskNotificationStatus = async (notificationId, status) => {
  const response = await apiRequest({
    endpoint: `clients/me/taskNotifications/${notificationId}/status`,
    method: HttpMethods.Patch,
    body: { status },
  });

  return extractResponseBody(response);
};

export const markAllMyTaskNotificationsRead = async () => {
  const response = await apiRequest({
    endpoint: 'clients/me/taskNotifications/readAll',
    method: HttpMethods.Patch,
  });

  return extractResponseBody(response);
};

export const getMyTaskNotificationsStreamUrl = () => {
  const protocol = Config.STREAM_SSE_API_PROTO || Config.API_PROTO;
  const address = Config.STREAM_SSE_API_URL;

  return `${protocol}://${address}/clients/me/taskNotifications/stream`;
};
